import React, { useState, useEffect, useMemo } from 'react';
import { TrendingUp, DollarSign, Target } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { CashFlowChart } from './CashFlowChart';
import { CashFlowTable } from './CashFlowTable';
import { CashFlowFilters } from './CashFlowFilters';
import { cn } from '@/lib/utils';
import { 
  DailyCashFlow, 
  CashFlowFilters as CashFlowFiltersType, 
  CashFlowViewMode, 
  CashFlowSummary 
} from '../../types/cash-flow';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { CashFlowService } from '@/services/cashFlowService';
import { supabase } from '@/lib/supabase';
import { formatCurrency } from '@/lib/utils/currencyUtils';

export function CashFlowPage() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<CashFlowViewMode>('both');
  const [availableAccounts, setAvailableAccounts] = useState<Array<{ id: string; name: string }>>([]);
  const [availableCategories, setAvailableCategories] = useState<Array<{ id: string; name: string }>>([]);
  
  // Initialize filters with current month
  const [filters, setFilters] = useState<CashFlowFiltersType>(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    return {
      periodo: {
        inicio: startOfMonth,
        fim: endOfMonth
      },
      visualizacao: 'diario',
      incluir_projecoes: true,
      contas_selecionadas: [],
      categorias_selecionadas: []
    };
  });

  // Real data from Supabase
  const [cashFlowData, setCashFlowData] = useState<DailyCashFlow[]>([]);

  // Load data when filters change
  useEffect(() => {
    const loadCashFlowData = async () => {
      if (!user) return;
      
      setIsLoading(true);
      
      try {
        // Buscar dados reais do Supabase usando o CashFlowService
        const data = await CashFlowService.calculateDailyCashFlow(user.id, filters);
        setCashFlowData(data);
      } catch (error) {
        console.error('Error loading cash flow data:', error);
        toast.error('Erro ao carregar dados do fluxo de caixa');
      } finally {
        setIsLoading(false);
      }
    };

    loadCashFlowData();
  }, [filters, user]);

  // Load available accounts and categories
  useEffect(() => {
    const loadAccountsAndCategories = async () => {
      if (!user) return;
      
      try {
        // Buscar contas de pagamento
        const paymentAccounts = await CashFlowService.fetchPaymentAccounts(user.id);
        const accounts = paymentAccounts.map(account => ({
          id: account.id,
          name: account.name || 'Sem nome'
        }));
        setAvailableAccounts(accounts);
        
        // Buscar categorias
        const { data: categories, error } = await supabase
          .from('categories')
          .select('id, name')
          .eq('user_id', user.id);
          
        if (error) throw error;
        
        const formattedCategories = categories?.map(category => ({
          id: category.id.toString(),
          name: category.name || 'Sem nome'
        })) || [];
        setAvailableCategories(formattedCategories);
      } catch (error) {
        console.error('Error loading accounts and categories:', error);
        toast.error('Erro ao carregar contas e categorias');
      }
    };

    loadAccountsAndCategories();
  }, [user]);

  // Calculate summary statistics
  const summary: CashFlowSummary = useMemo(() => {
    if (!cashFlowData.length) {
      return {
        periodo: filters.periodo,
        totais: {
          recebimentos: 0,
          pagamentos: 0,
          transferencias_entrada: 0,
          transferencias_saida: 0,
          saldo_liquido: 0
        },
        saldo_inicial: 0,
        saldo_final: 0,
        dias_com_dados: 0
      };
    }

    const totais = cashFlowData.reduce((acc, item) => ({
      recebimentos: acc.recebimentos + item.recebimentos,
      pagamentos: acc.pagamentos + item.pagamentos,
      transferencias_entrada: acc.transferencias_entrada + item.transferencias_entrada,
      transferencias_saida: acc.transferencias_saida + item.transferencias_saida,
      saldo_liquido: 0 // Will calculate below
    }), {
      recebimentos: 0,
      pagamentos: 0,
      transferencias_entrada: 0,
      transferencias_saida: 0,
      saldo_liquido: 0
    });

    const saldo_inicial = cashFlowData[0]?.saldo_inicial || 0;
    const saldo_final = cashFlowData[cashFlowData.length - 1]?.saldo_final || 0;
    totais.saldo_liquido = saldo_final - saldo_inicial;

    return {
      periodo: filters.periodo,
      totais,
      saldo_inicial,
      saldo_final,
      dias_com_dados: cashFlowData.length
    };
  }, [cashFlowData, filters.periodo]);


  const formatCurrencyWithSign = (value: number) => {
    const formatted = formatCurrency(Math.abs(value));
    return value >= 0 ? `+${formatted}` : `-${formatted}`;
  };

  const handleDateClick = (date: Date) => {
    // TODO: Open detailed view for specific date
    console.log('Date clicked:', date);
    toast.info(`Detalhes para ${new Intl.DateTimeFormat('pt-BR').format(date)}`);
  };

  const handleRowClick = (cashFlow: DailyCashFlow) => {
    // TODO: Open detailed view for specific day
    console.log('Row clicked:', cashFlow);
    handleDateClick(cashFlow.date);
  };

  const handleExport = () => {
    // TODO: Implement export functionality
    console.log('Exporting cash flow data...', { filters, data: cashFlowData });
    toast.success('Exportação iniciada. O arquivo será baixado em breve.');
  };

  return (
    <div className="space-y-6">


      {/* Summary Cards */}
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="card-hover overflow-hidden transition-all duration-300 hover:shadow-lg group border-0 border-l-4 border-l-finance-green hover:shadow-finance-green/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-semibold text-muted-foreground group-hover:text-foreground transition-colors">
                Total Recebimentos
              </CardTitle>
              <div className="rounded-xl p-2.5 border transition-all duration-200 group-hover:scale-110 bg-finance-green/10 text-finance-green border-finance-green/20 hover:bg-finance-green/20">
                <TrendingUp className="h-5 w-5" />
              </div>
            </CardHeader>
            <CardContent className="pb-4">
              <div className="text-3xl font-bold text-finance-green mb-2 group-hover:scale-105 transition-transform duration-200">
                {formatCurrency(summary.totais.recebimentos)}
              </div>
              <p className="text-xs text-muted-foreground">
                {summary.dias_com_dados} dias no período
              </p>
            </CardContent>
          </Card>

          <Card className="card-hover overflow-hidden transition-all duration-300 hover:shadow-lg group border-0 border-l-4 border-l-finance-red hover:shadow-finance-red/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-semibold text-muted-foreground group-hover:text-foreground transition-colors">
                Total Pagamentos
              </CardTitle>
              <div className="rounded-xl p-2.5 border transition-all duration-200 group-hover:scale-110 bg-finance-red/10 text-finance-red border-finance-red/20 hover:bg-finance-red/20">
                <TrendingUp className="h-5 w-5 rotate-180" />
              </div>
            </CardHeader>
            <CardContent className="pb-4">
              <div className="text-3xl font-bold text-finance-red mb-2 group-hover:scale-105 transition-transform duration-200">
                {formatCurrency(summary.totais.pagamentos)}
              </div>
              <p className="text-xs text-muted-foreground">
                Média diária: {formatCurrency(summary.totais.pagamentos / Math.max(summary.dias_com_dados, 1))}
              </p>
            </CardContent>
          </Card>

          <Card className={cn(
            "card-hover overflow-hidden transition-all duration-300 hover:shadow-lg group border-0 border-l-4",
            summary.saldo_final >= 0
              ? "border-l-finance-green hover:shadow-finance-green/20"
              : "border-l-finance-red hover:shadow-finance-red/20"
          )}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-semibold text-muted-foreground group-hover:text-foreground transition-colors">
                Saldo Atual
              </CardTitle>
              <div className={cn(
                "rounded-xl p-2.5 border transition-all duration-200 group-hover:scale-110",
                summary.saldo_final >= 0
                  ? "bg-finance-green/10 text-finance-green border-finance-green/20 hover:bg-finance-green/20"
                  : "bg-finance-red/10 text-finance-red border-finance-red/20 hover:bg-finance-red/20"
              )}>
                <DollarSign className="h-5 w-5" />
              </div>
            </CardHeader>
            <CardContent className="pb-4">
              <div className={cn(
                "text-3xl font-bold mb-2 group-hover:scale-105 transition-transform duration-200",
                summary.saldo_final >= 0 ? 'text-finance-green' : 'text-finance-red'
              )}>
                {formatCurrency(summary.saldo_final)}
              </div>
              <p className="text-xs text-muted-foreground">
                Variação: {formatCurrencyWithSign(summary.totais.saldo_liquido)}
              </p>
            </CardContent>
          </Card>

          <Card className={cn(
            "card-hover overflow-hidden transition-all duration-300 hover:shadow-lg group border-0 border-l-4",
            summary.totais.saldo_liquido >= 0
              ? "border-l-finance-green hover:shadow-finance-green/20"
              : "border-l-finance-red hover:shadow-finance-red/20"
          )}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-semibold text-muted-foreground group-hover:text-foreground transition-colors">
                Fluxo Líquido
              </CardTitle>
              <div className={cn(
                "rounded-xl p-2.5 border transition-all duration-200 group-hover:scale-110",
                summary.totais.saldo_liquido >= 0
                  ? "bg-finance-green/10 text-finance-green border-finance-green/20 hover:bg-finance-green/20"
                  : "bg-finance-red/10 text-finance-red border-finance-red/20 hover:bg-finance-red/20"
              )}>
                <Target className="h-5 w-5" />
              </div>
            </CardHeader>
            <CardContent className="pb-4">
              <div className={cn(
                "text-3xl font-bold mb-2 group-hover:scale-105 transition-transform duration-200",
                summary.totais.saldo_liquido >= 0 ? 'text-finance-green' : 'text-finance-red'
              )}>
                {formatCurrencyWithSign(summary.totais.saldo_liquido)}
              </div>
              <p className="text-xs text-muted-foreground">
                {summary.totais.saldo_liquido >= 0 ? 'Fluxo positivo' : 'Fluxo negativo'}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Filters */}
      <div>
        <Card className="card-elevated">
          <CardContent className="pt-6">
            <CashFlowFilters
              filters={filters}
              onFiltersChange={setFilters}
              availableAccounts={availableAccounts}
              availableCategories={availableCategories}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              onExport={handleExport}
              loading={isLoading}
            />
          </CardContent>
        </Card>
      </div>

  
      {/* Chart and/or Table */}
      <div className="space-y-6">
        {(viewMode === 'chart' || viewMode === 'both') && (
          <CashFlowChart
            data={cashFlowData}
            height={450}
            showProjections={filters.incluir_projecoes}
            onDateClick={handleDateClick}
            loading={isLoading}
            title="Fluxo de Caixa - Visualização Gráfica"
            description="Acompanhe as movimentações financeiras e tendências ao longo do tempo"
          />
        )}

        {(viewMode === 'table' || viewMode === 'both') && (
          <CashFlowTable
            data={cashFlowData}
            onRowClick={handleRowClick}
            sortable={true}
            filterable={true}
            exportable={true}
            loading={isLoading}
            title="Fluxo de Caixa - Detalhamento Diário"
            description="Dados detalhados de cada dia com possibilidade de ordenação e filtros"
          />
        )}
      </div>

      {/* Empty State */}
      {!isLoading && cashFlowData.length === 0 && (
        <div className="section-delimited">
          <Card className="card-elevated">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <TrendingUp className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Nenhum dado encontrado</h3>
              <p className="text-sm text-muted-foreground text-center max-w-sm">
                Não há movimentações financeiras para o período selecionado.
                Ajuste os filtros ou adicione transações ao sistema.
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}